import { Module } from 'vuex'
import { RootState } from '@/store/types'
import { IAddonsPayload, IAddonsResponse } from '../services/types'
import { actionTypes, IAddonsFilters, IAddonsState } from './types'
import AddonsService from '../services'
import { ITypeOrmPaginationTemplate } from '@/utils/interfaces/typeormPagination'

const state: IAddonsState = {
	addons: {
		data: [],
		meta: {
			current_page: 1,
			from: 0,
			last_page: 1,
			per_page: 10,
			to: 0,
			total: 0,
		},
		loading: false,
	},

	openModalCreateAddons: false,
	openModalUpdateAddons: false,
	addonIsLoading: false,
	addonIsSaving: false,

	selectedAddon: {
		id: '',
		name: '',
		description: '',
		price: '',
		created_by_id: '',
		created_by_type: '',
		active: false,
		organizacao_id: undefined,
		created_at: '',
		updated_at: '',
	},

	filters: {
		page: 1,
		limit: 10,
		search: '',
	},
}

const mutations = {
	SET_ADDONS_LOADING(state: IAddonsState, loading: boolean) {
		state.addonIsLoading = loading
	},
	SET_ADDONS(state: IAddonsState, payload: ITypeOrmPaginationTemplate<IAddonsResponse>) {
		state.addons.data = payload.data
		state.addons.meta = payload.meta
	},
	SET_ADDONS_SELECTED(state: IAddonsState, payload: IAddonsResponse) {
		state.selectedAddon = payload
	},
	CLEAR_ADDONS_SELECTED(state: IAddonsState) {
		state.selectedAddon = {
			id: '',
			name: '',
			description: '',
			price: '',
			created_by_id: '',
			created_by_type: '',
			active: false,
			organizacao_id: undefined,
			created_at: '',
			updated_at: '',
		}
	},
	SET_MODAL_CREATE_ADDONS(state: IAddonsState, open: boolean) {
		state.openModalCreateAddons = open
	},
	SET_MODAL_UPDATE_ADDONS(state: IAddonsState, open: boolean) {
		state.openModalUpdateAddons = open
	},
}

const actions = {
	async [actionTypes.GET_ADDONS](
		{ commit, state }: any,
		payload?: { resetPage?: boolean; filters?: IAddonsFilters }
	) {
		commit('SET_ADDONS_LOADING', true)

		try {
			let filters = payload?.filters || state.filters

			if (payload?.resetPage) {
				filters = { ...filters, page: 1 }
			}

			const { data } = await AddonsService.getAddons(filters)

			commit('SET_ADDONS', data)
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	async [actionTypes.GET_ADDON]({ commit }: any, id: string) {
		commit('SET_ADDONS_LOADING', true)

		try {
			// Por enquanto, vamos buscar o addon da lista existente
			// Em uma implementação completa, você teria um endpoint específico para buscar um addon
			const { data } = await AddonsService.getAddons({ page: 1, limit: 1000, search: '' })
			const addon = data.data.find((item: IAddonsResponse) => item.id === id)

			if (addon) {
				commit('SET_ADDONS_SELECTED', addon)
			}
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	async [actionTypes.CREATE_ADDON]({ dispatch, commit, state }: any, payload: IAddonsPayload) {
		commit('SET_ADDONS_LOADING', true)

		try {
			await AddonsService.createAddon(payload)
			// Manter os filtros atuais, mas resetar para página 1 para mostrar o novo item
			dispatch(actionTypes.GET_ADDONS, { resetPage: true })
			commit('SET_MODAL_CREATE_ADDONS', false)
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	async [actionTypes.UPDATE_ADDON](
		{ dispatch, commit }: any,
		{ id, payload }: { id: string; payload: IAddonsPayload }
	) {
		commit('SET_ADDONS_LOADING', true)

		try {
			await AddonsService.updateAddon({ id, payload })
			// Manter os filtros atuais para não perder a posição na lista
			dispatch(actionTypes.GET_ADDONS)
			commit('SET_MODAL_CREATE_ADDONS', false)
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	async [actionTypes.DELETE_ADDON]({ dispatch, commit }: any, id: string) {
		commit('SET_ADDONS_LOADING', true)

		try {
			await AddonsService.removeAddon(id)
			dispatch(actionTypes.GET_ADDONS, { page: 1, limit: 10, search: '' })
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	async [actionTypes.UPDATE_ADDON_ACTIVE](
		{ dispatch, commit }: any,
		{ id, active }: { id: string; active: boolean }
	) {
		commit('SET_ADDONS_LOADING', true)

		try {
			await AddonsService.changeActiveAddon({ id, active })
			dispatch(actionTypes.GET_ADDONS)
		} finally {
			commit('SET_ADDONS_LOADING', false)
		}
	},
	[actionTypes.SET_MODAL_CREATE_ADDONS]({ commit }: any, open: boolean) {
		commit('SET_MODAL_CREATE_ADDONS', open)
	},

	[actionTypes.CLEAR_ADDONS_SELECTED]({ commit }: any) {
		commit('CLEAR_ADDONS_SELECTED')
	},
}

const getters = {
	addonsList: (state: IAddonsState) => state.addons.data,
	addonsMeta: (state: IAddonsState) => state.addons.meta,
	addonsLoading: (state: IAddonsState) => state.addons.loading,
	selectedAddon: (state: IAddonsState) => state.selectedAddon,
	openModalCreateAddons: (state: IAddonsState) => state.openModalCreateAddons,
	openModalUpdateAddons: (state: IAddonsState) => state.openModalUpdateAddons,
}

export const Addons: Module<IAddonsState, RootState> = {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
}

export default Addons
